import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance, { isTokenValid } from '../utils/axiosConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  console.log('AuthProvider: Initializing', { user, loading });

  useEffect(() => {
    console.log('AuthProvider: useEffect triggered');
    const token = localStorage.getItem('token');
    console.log('AuthProvider: Token from localStorage', { hasToken: !!token, tokenLength: token?.length });
    
    if (token) {
      console.log('AuthProvider: Verifying existing token');
      if (isTokenValid()) {
        // Verify token with server
        verifyToken();
      } else {
        console.log('AuthProvider: Token invalid, clearing auth data');
        setLoading(false);
      }
    } else {
      console.log('AuthProvider: No token found, setting loading to false');
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    console.log('AuthProvider: verifyToken starting');
    try {
      console.log('AuthProvider: Making verify request to /api/auth/verify');
      const response = await axiosInstance.get('/auth/verify');
      console.log('AuthProvider: Token verification successful', response.data);
      setUser(response.data.user);
      console.log('AuthProvider: User state updated after verification', response.data.user);
    } catch (error) {
      console.error('AuthProvider: Token verification failed', error);
      // Auto-logout is handled by axios interceptor
      setUser(null);
    } finally {
      console.log('AuthProvider: Setting loading to false after verification');
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    console.log('AuthContext: Login function called', { username, passwordLength: password?.length });
    
    try {
      console.log('AuthContext: Making login request to /auth/login');
      const response = await axiosInstance.post('/auth/login', { email: username, password });
      console.log('AuthContext: Login request successful', response.status, response.data);
      const { token, user: userData } = response.data;
      
      console.log('AuthContext: Storing token and user data');
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('AuthContext: Setting user state', userData);
      setUser(userData);
      console.log('AuthContext: User state has been set, login complete');
      
      return userData;
    } catch (error) {
      console.error('AuthContext: Login request failed', error);
      console.error('AuthContext: Error response:', error.response);
      throw error.response?.data || { message: 'Login failed' };
    }
  };

  const logout = () => {
    console.log('AuthContext: Manual logout triggered');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('dashboardCache');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};